import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { TenantProvider } from './TenantContext';
import { ThemeProvider } from './ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import Overview from './pages/Overview';
import Analytics from './pages/Analytics';
import Reports from './pages/Reports';
import RadiusServers from './pages/RadiusServers';
import Tenants from './pages/Tenants';
import Users from './pages/Users';
import Profiles from './pages/Profiles';
import NasDevices from './pages/NasDevices';
import VpnServers from './pages/VpnServers';
import VpnWireguard from './pages/VpnWireguard';
import VpnIpsec from './pages/VpnIpsec';
import SyncStatus from './pages/SyncStatus';
import SyncLog from './pages/SyncLog';
import AuthLog from './pages/AuthLog';
import ChangeLog from './pages/ChangeLog';
import WorkerQuery from './pages/WorkerQuery';
import OnlineUsers from './pages/OnlineUsers';
import AdminUsers from './pages/AdminUsers';
import ApiTokens from './pages/ApiTokens';
import Login from './pages/Login';

/* ── requires authentication ── */
function ProtectedLayout() {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // route yang tidak boleh diakses role tertentu (proteksi UX; backend tetap sumber kebenaran)
  const blockedFor = { '/radius-servers': ['operator', 'user'], '/worker-query': ['operator', 'user', 'admin'], '/vpn/servers': ['operator', 'user'] };
  const RoleGate = ({ path, children }) => {
    if (blockedFor[path]?.includes(user?.role)) return <Navigate to="/" replace />;
    return children;
  };

  return (
    <TenantProvider>
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        <Sidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed(c => !c)}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <Topbar onMenuClick={() => setMobileOpen(true)} collapsed={collapsed} onToggleCollapse={() => setCollapsed(c => !c)} />
          <main style={{ flex: 1, overflow: 'auto', padding: 'clamp(12px, 3vw, 28px)' }}>
            <Routes>
              <Route path="/"               element={<Overview />} />
              <Route path="/radius-servers" element={<RoleGate path="/radius-servers"><RadiusServers /></RoleGate>} />
              <Route path="/tenants"        element={<Tenants />} />
              <Route path="/users"          element={<Users />} />
              <Route path="/profiles"       element={<Profiles />} />
              <Route path="/nas"            element={<NasDevices />} />
              <Route path="/vpn/servers"    element={<RoleGate path="/vpn/servers"><VpnServers /></RoleGate>} />
              <Route path="/vpn/wireguard"  element={<VpnWireguard />} />
              <Route path="/vpn/ipsec"      element={<VpnIpsec />} />
              <Route path="/sync"           element={<Navigate to="/sync/status" replace />} />
              <Route path="/sync/status"    element={<SyncStatus />} />
              <Route path="/sync/log"       element={<SyncLog />} />
              <Route path="/sync/auth-log"  element={<AuthLog />} />
              <Route path="/sync/change-log" element={<ChangeLog />} />
              <Route path="/analytics"      element={<Analytics />} />
              <Route path="/reports"        element={<Reports />} />
              <Route path="/worker-query"   element={<RoleGate path="/worker-query"><WorkerQuery /></RoleGate>} />
              <Route path="/online-users"   element={<OnlineUsers />} />
              <Route path="/admin-users"    element={<AdminUsers />} />
              <Route path="/api-tokens"     element={<ApiTokens />} />
              <Route path="*"               element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </TenantProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/*"    element={<ProtectedLayout />} />
          </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}